import { Add, Edit, ManageAccounts, Person } from '@mui/icons-material';
import { Grid, Typography, List, ListItem, ListItemIcon, ListItemText, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { ContentSection } from 'src/shared/components/organisms/contentSection';
import { useGlobals } from 'src/shared/contexts/appContext';
import { useState } from 'react';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { Member } from '../models/member';
import { MemberMeFamilyModal } from './memberMeFamilyModal';

type MemberMeFamilyMembersProps = {
  member: Member;
  showEditButtons?: boolean | undefined;
  refetchMemberCallback: () => void;
};

export const MemberMeFamilyMembers = (props: MemberMeFamilyMembersProps) => {
  const { member, showEditButtons = true, refetchMemberCallback } = props;
  const { t } = useTranslation(['members']);
  const { currentUser, currentMember } = useGlobals();

  const [openFamilyEditModal, setOpenFamilyEditModal] = useState(false);

  const hasMemberUpdatePermission = getPermission(currentUser, PermissionEnum.MemberUpdate);

  const hasFamilyMembers = () => {
    return !!(member?.familyMembers && member.familyMembers.length > 0);
  };

  const onEditButtonHandler = () => {
    setOpenFamilyEditModal(true);
  };

  return (
    <Grid item xs={12}>
      <ContentSection
        header={t('member:memberDetails.houseHoldMember')}
        showHeaderCounter={hasFamilyMembers()}
        headerCounter={member.familyMembers?.length}
        button={
          <>
            {showEditButtons && (
              <Button
                variant='outlined'
                size='small'
                onClick={onEditButtonHandler}
                endIcon={hasFamilyMembers() ? <Edit fontSize='small' /> : <Add fontSize='small' />}
                disabled={!currentMember?.enableAddFamily || !hasMemberUpdatePermission}
              >
                {hasFamilyMembers() ? t('common:button.edit') : t('common:button.create')}
              </Button>
            )}
          </>
        }
      >
        <List dense>
          {member.familyMembers?.map((item) => (
            <ListItem sx={{ py: 0, pl: 0.5 }} key={item.member_id}>
              <ListItemIcon sx={{ width: '2rem', maxWidth: '2rem', minWidth: '2rem' }}>
                {item.mainMember && <ManageAccounts fontSize='small' />}
                {!item.mainMember && <Person fontSize='small' />}
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    {currentUser ? (
                      <Link to={`${RoutesEnum.MemberDetails}/${item.member_id}`}>{`${item.firstName} ${item.lastName}`}</Link>
                    ) : (
                      <Typography component='span'>{`${item.firstName} ${item.lastName}`}</Typography>
                    )}
                    {item.mainMember && (
                      <Typography component='span' variant='caption' sx={{ fontWeight: 500, ml: 0.5 }}>
                        ({t('member:memberDetails.headMember')})
                      </Typography>
                    )}
                  </>
                }
              />
            </ListItem>
          ))}
        </List>
      </ContentSection>
      {openFamilyEditModal && member && (
        <MemberMeFamilyModal
          modalOpen={openFamilyEditModal}
          member={member}
          onClose={() => {
            setOpenFamilyEditModal(false);
            refetchMemberCallback();
          }}
        />
      )}
    </Grid>
  );
};
